import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  IconButton,
  Paper,
  useTheme,
  Box,
  Typography,
  TextField
} from "@mui/material";

import CloseIconImage from "../assets/icons/Close Square.svg";
import SendIcon from "../assets/icons/Send.svg";
import BackIcon from "../assets/icons/ArrowLeft.svg";
import Attachment from "../assets/icons/Attachment.svg";
import ReceiverFleetGPT from "../assets/icons/ReceiverFleetGPT.svg";

import KeyIcon from '@mui/icons-material/Key';
import { API_ENDPOINTS } from '../api/endpoints';
import useAxios from '../api/useAxios';
import { useAuth } from '../authContext/useAuth';
import DOMPurify from 'dompurify';

interface FleetgptLayoutProps {
  onBack: () => void;
  onClose: () => void;
  initialMessage?: string;
  questionId: number;
  customer_id: string;
  vehicleId?: string;
  chatInfo?: {
    user_id: string;
    conversation_id: string;
    message: string;
  };
}


type WarningType = "Seat Belt" | "Drowsiness" | "Distracted";
type FollowUpAction = "explore_patterns" | "no_thanks";

type Message = {
  sender: "user" | "assistant";
  content: string;
  details?: string[];
  isHTML?: boolean;
  actions?: { label: string; type: WarningType }[];
  followUpActions?: { label: string; type: FollowUpAction }[];
};

interface Payload {
  question_id: number;
  fleet_manager_id: string;
  vehicle_id?: string;
  warning_type?: number;
}




export default function FleetgptChatLayout({
  onBack,
  onClose,
  initialMessage,
  questionId,
  customer_id,
  vehicleId,
  chatInfo,
}: FleetgptLayoutProps) {
  // Use the actual prop, not the type
  console.log('chatInfoChat:', chatInfo);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [inputValue, setInputValue] = useState(initialMessage || "");
  const [messages, setMessages] = useState<Message[]>([]);
  const [awaitingVehicleId, setAwaitingVehicleId] = useState(false);
  const [showCuriosityPrompt, setShowCuriosityPrompt] = useState(false);
  const [showLockedContent, setShowLockedContent] = useState(false);
  const [showConfirmationPrompt, setShowConfirmationPrompt] = useState(false);
  const [showEmailInputPrompt, setShowEmailInputPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [isNoThanksAcknowledged, setIsNoThanksAcknowledged] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const noThanksTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const LOCAL_STORAGE_KEY = "fleetgpt_chat_messages";

  const { user } = useAuth();

  const {
    data: fleetGptFreeVersionData,
    error: fleetGptFreeVersionError,
    loading: fleetGptFreeVersionLoading,
    fetchData: fetchFleetGptFreeVersionData,
  } = useAxios<any>(API_ENDPOINTS.fleetgpt.FLEETGPT_FREEVERSION, "POST");

  const appendMessage = (msg: Message) => {
    setMessages(prev => {
      const updated = [...prev, msg];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };


  const sendPayload = async (payload: Payload, askFollowUp = false, askCuriosity = false) => {
    setIsLoading(true);
    try {
      // Always use chatInfo for user_id and conversation_id
      const userId = chatInfo?.user_id || payload.fleet_manager_id;
      const conversationId = chatInfo?.conversation_id || '';
      // Always use the user's input for message
      const message = payload && (payload as any).message ? (payload as any).message : inputValue;

      const url = `http://13.203.68.232:8000/chat?user_id=${encodeURIComponent(userId)}&conversation_id=${encodeURIComponent(conversationId)}&message=${encodeURIComponent(message)}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "accept": "application/json" },
        body: '',
      });
      const data = await response.json();
      const reply = data.message || "No response message provided.";
      appendMessage({ sender: "assistant", content: reply });
      setIsLoading(false);

      // After every 1000 user messages, show lock and interest prompt
      const userMessagesCount = messages.filter(m => m.sender === "user").length + 1; // +1 for current
      if (userMessagesCount % 1000 === 0) {
        setShowLockedContent(true);
        setIsInputDisabled(true);
      }
    } catch (error) {
      console.error("Error fetching answer:", error);
      appendMessage({ sender: "assistant", content: "Error fetching answer." });
      setIsLoading(false);
    }
  };

  const isCombinedWarningQuery = (text: string) => {
    return /recent warning/i.test(text) || /last warning/i.test(text);
  };

  const isVehicleQuery = (text: string) => {
    return /where is my vehicle\??/i.test(text);
  };



  const handleUnlockClick = () => {
    setShowLockedContent(false);
    setShowConfirmationPrompt(true);
    setIsInputDisabled(true); // Keep input disabled until user chooses
  };

  const handleContinueFree = async () => {
    const payload = {
      customerId: user?.customer_id || '',
      increasedValidity: "1",
    };

    try {
      await fetchFleetGptFreeVersionData(payload);
    } catch (error) {
      console.error('Failed to submit free version access:', error);
    }

    setShowConfirmationPrompt(false);
    appendMessage({ sender: "user", content: "Great! You can now explore basic patterns. For advanced features, consider upgrading." });
    setIsInputDisabled(false); // Re-enable input after continuing with free version
  };

  const handleShowInterest = () => {
    setShowConfirmationPrompt(false);
    setShowEmailInputPrompt(true);
    setIsInputDisabled(false); // Enable input for email
    setInputValue(''); // Clear main input value, as it will be used for email
  };

  const sendEmptyEmailPayloadAndClose = async (showLimitExceededMessage: boolean = false) => {
    const payload = {
      customerId: user?.customer_id || '',
      increasedValidity: "1",
    };

    try {
      await fetchFleetGptFreeVersionData(payload);
      console.log('No-thanks payload sent.');
      if (showLimitExceededMessage) {
        appendMessage({
          sender: "assistant",
          content: "Your limit has been exceeded. Please contact us at marketing@novushitech.com for more information."
        });
      }
      setInputValue("");
      setIsInputDisabled(true); // Disable input after timeout/limit message
    } catch (error) {
      console.error('Failed to submit empty email payload:', error);
      appendMessage({ sender: "assistant", content: "There was an error processing your request. Closing chat." });
      setInputValue("");
      setIsInputDisabled(true); // Disable input even on error
      setTimeout(() => {
        onClose();
      }, 3000);
    }
  };

  const handleEmailSubmit = async () => {
    if (noThanksTimeoutRef.current) {
      clearTimeout(noThanksTimeoutRef.current);
      noThanksTimeoutRef.current = null;
    }

    const emailText = inputValue.trim();

    if (!emailText) {
      sendEmptyEmailPayloadAndClose(true);
      setTimeout(() => {
        onClose();
      }, 1000);
      return;
    }

    const payload = {
      customerId: user?.customer_id || '',
      increasedValidity: "1",
      emailId: emailText,
    };

    try {
      await fetchFleetGptFreeVersionData(payload);
      appendMessage({ sender: "user", content: `My email is ${emailText}` });
      appendMessage({ sender: "assistant", content: `Thank you for your interest! We've received your email at ${emailText}. Our team will reach out to you shortly.` });
      setInputValue('');
      setIsInputDisabled(false); // Re-enable input after email submission
      setShowEmailInputPrompt(false);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to submit email for advanced features:', error);
      appendMessage({ sender: "assistant", content: "There was an error submitting your email. Please try again." });
    }
  };

  const handleSend = async () => {
    if (noThanksTimeoutRef.current) {
      clearTimeout(noThanksTimeoutRef.current);
      noThanksTimeoutRef.current = null;
    }

    const text = inputValue.trim();

    if (showEmailInputPrompt) {
      handleEmailSubmit();
      return;
    }

    if (!text) return;

    appendMessage({ sender: "user", content: text });
    setInputValue("");
    setIsInputDisabled(false);

    // Always send the user's input as the message
    const payload: Payload = {
      question_id: questionId,
      fleet_manager_id: customer_id,
      message: text,
    } as any;
    sendPayload(payload);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showEmailInputPrompt) {
        handleEmailSubmit();
      } else {
        handleSend();
      }
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showLockedContent, showConfirmationPrompt, showEmailInputPrompt]);


  // Simplified Effect to manage input disabled state


  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      setMessages(JSON.parse(saved));
    }

    if (initialMessage?.trim() && messages.length === 0) {
      const text = initialMessage.trim();
      appendMessage({ sender: "user", content: text });
      setInputValue("");

      if (isVehicleQuery(text)) {
        appendMessage({ sender: "assistant", content: "Please enter Vehicle ID" });
        setAwaitingVehicleId(true);
      } else if (isCombinedWarningQuery(text)) {
        const payload: Payload = {
          question_id: questionId,
          fleet_manager_id: customer_id,
        };
        sendPayload(payload, true);
      } else {
        const payload: Payload = {
          question_id: questionId,
          fleet_manager_id: customer_id,
        };
        if (vehicleId) payload.vehicle_id = vehicleId;
        sendPayload(payload);
      }
    }
  }, []);

  const handleClose = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    if (noThanksTimeoutRef.current) {
      clearTimeout(noThanksTimeoutRef.current);
      noThanksTimeoutRef.current = null;
    }
    onClose();
  };

  const shouldShowChatInput = !showEmailInputPrompt && !showLockedContent && !showConfirmationPrompt;

  const markdownToHTML = (text: string) => {
    let html = text;
    
    // Replace markdown headers
    html = html.replace(/^### (.*$)/gm, '<h3 class="markdown-h3">$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2 class="markdown-h2">$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1 class="markdown-h1">$1</h1>');
    
    // Replace **bold** with <strong>bold</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Parse markdown tables
    const tableRegex = /(\|[^|\n]*\|[^|\n]*\|[^\n]*\n\|[-| ]*\|[-| ]*\|[^\n]*\n(?:\|[^|\n]*\|[^|\n]*\|[^\n]*\n?)*)/g;
    
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length < 3) return match;
      
      const headerLine = lines[0];
      const separatorLine = lines[1];
      const dataLines = lines.slice(2);
      
      // Parse header
      const headers = headerLine.split('|').map(h => h.trim()).filter(h => h);
      
      // Parse data rows
      const rows = dataLines.map(line => 
        line.split('|').map(cell => cell.trim()).filter(cell => cell)
      );
      
      // Generate HTML table
      let tableHTML = '<table class="markdown-table">';
      
      // Header
      tableHTML += '<thead><tr>';
      headers.forEach(header => {
        tableHTML += `<th>${header}</th>`;
      });
      tableHTML += '</tr></thead>';
      
      // Body
      tableHTML += '<tbody>';
      rows.forEach(row => {
        if (row.length > 0) {
          tableHTML += '<tr>';
          row.forEach((cell, index) => {
            if (index < headers.length) {
              tableHTML += `<td>${cell}</td>`;
            }
          });
          tableHTML += '</tr>';
        }
      });
      tableHTML += '</tbody></table>';
      
      return tableHTML;
    });
    
    return html;
  };

  return (
    <>
      <style>{`
        /* Reset default margins for better control */
        p, div, span {
          margin: 0;
          padding: 0;
        }
        
        /* Compact paragraph spacing */
        p {
          margin: 2px 0;
          line-height: 1.3;
        }
        
        /* Markdown Headers */
        .markdown-h1 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 2px 0 1px 0;
          color: ${isDark ? '#f1f1f1' : '#1a1a1a'};
          line-height: 1.1;
        }
        
        .markdown-h2 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 1px 0 1px 0;
          color: ${isDark ? '#e0e0e0' : '#2a2a2a'};
          line-height: 1.2;
        }
        
        .markdown-h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 1px 0 1px 0;
          color: ${isDark ? '#d0d0d0' : '#3a3a3a'};
          line-height: 1.2;
        }
        
        .markdown-table {
          width: 100%;
          border-collapse: collapse;
          margin: 2px 0 2px 0;
          font-size: 0.8rem;
          border-radius: 6px;
          overflow: hidden;
          background-color: transparent;
          border: 1px solid ${isDark ? '#444' : '#e0e0e0'};
        }
        
        .markdown-table th {
          background-color: ${isDark ? 'rgba(68, 68, 68, 0.3)' : 'rgba(0, 0, 0, 0.05)'};
          color: ${isDark ? '#f1f1f1' : '#555'};
          font-weight: 600;
          padding: 6px 8px;
          text-align: left;
          border-bottom: 1px solid ${isDark ? '#444' : '#e0e0e0'};
          font-size: 0.75rem;
        }
        
        .markdown-table td {
          padding: 6px 8px;
          border-bottom: 1px solid ${isDark ? 'rgba(68, 68, 68, 0.3)' : 'rgba(224, 224, 224, 0.5)'};
          color: ${isDark ? '#f1f1f1' : '#333'};
          background-color: transparent;
        }
        
        .markdown-table tr:nth-child(even) td {
          background-color: ${isDark ? 'rgba(68, 68, 68, 0.1)' : 'rgba(0, 0, 0, 0.02)'};
        }
        
        .markdown-table tr:hover td {
          background-color: ${isDark ? 'rgba(68, 68, 68, 0.2)' : 'rgba(0, 0, 0, 0.04)'};
        }
        
        .markdown-table tr:last-child td {
          border-bottom: none;
        }

        /* Ensure table fits within chat bubble */
        .markdown-table {
          max-width: 100%;
          box-sizing: border-box;
        }
        
        .markdown-table th,
        .markdown-table td {
          word-wrap: break-word;
          max-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
      <Paper
        elevation={3}
        className="w-full max-w-[384px] shadow-lg rounded-2xl border"
        sx={{
          bgcolor: isDark ? "#1e1e1e" : "#fff",
          borderColor: isDark ? "#444" : "#ddd",
          color: isDark ? "#f1f1f1" : "#1e1e1e",
          display: "flex",
          flexDirection: "column",
          height: "600px",
        }}
      >
        {/* Header */}
        <div
          className="px-4 py-3 flex items-center justify-between border-b"
          style={{ borderColor: isDark ? "#333" : "#ddd" }}
        >
          <Button
            onClick={onBack}
            variant="text"
            size="small"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#5547DD",
              textTransform: "none",
              ":hover": { backgroundColor: isDark ? "#2a2a2a" : "#f3f0ff" },
            }}
          >
            <img
              src={BackIcon}
              alt="Back"
              className="h-4 w-4"
              style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }}
            />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <IconButton size="small" onClick={handleClose}>
              <img
                src={CloseIconImage}
                alt="Close"
                className="h-4 w-4"
                style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }}
              />
            </IconButton>
          </div>
        </div>

        {/* Chat Body */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 flex flex-col items-center">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} w-full`}
            >
              {msg.sender === "assistant" && (
                <div className="flex-shrink-0 mr-2">
                  <img
                    src={ReceiverFleetGPT}
                    alt="FleetGPT"
                    className="h-6 w-6"
                    style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }}
                  />
                </div>
              )}

              <div
                className={`rounded-xl px-3 py-2 max-w-[80%] text-sm whitespace-pre-line ${msg.sender === "user"
                  ? isDark
                    ? "bg-[#333] text-white"
                    : "bg-gray-100 text-gray-800"
                  : isDark
                    ? "bg-[#2a2a2a] border border-[#444]"
                    : "bg-white border border-gray-200 text-gray-800"
                  }`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(markdownToHTML(msg.content)),
                  }}
                />
                {msg.details && (
                  <ul className="list-disc pl-5 mt-2">
                    {msg.details.map((d: string, i: number) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start w-full">
              <div className="flex-shrink-0 mr-2">
                <img src={ReceiverFleetGPT} alt="FleetGPT" className="h-6 w-6 animate-spin" style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }} />
              </div>
              <div className="rounded-xl px-4 py-3 max-w-[80%] text-sm bg-gray-100 text-gray-800">
                <p>
                  <span className="animate-pulse">Thinking...</span>
                </p>
              </div>
            </div>
          )}
          {/* Modern Locked Content */}
          {showLockedContent && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
                mt: 2,
                bgcolor: isDark ? "#2a2a2a" : "#f0f0f0",
                borderRadius: 4,
                boxShadow: theme.shadows[3],
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                width: '80%',
                margin: '0 auto',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Free Message Limit Reached
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: 'center' }}>
                You’ve reached your free message limit. Would you like to unlock advanced features?
              </Typography>
              <Button
                onClick={handleUnlockClick}
                variant="contained"
                color="primary"
                sx={{ textTransform: 'none', mb: 1 }}
              >
                Unlock Advanced Features
              </Button>
            </Box>
          )}

          {/* Modern Interest Prompt */}
          {showConfirmationPrompt && !isNoThanksAcknowledged && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
                mt: 2,
                bgcolor: isDark ? "#2a2a2a" : "#f0f0f0",
                borderRadius: 4,
                boxShadow: theme.shadows[3],
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                width: '80%',
                margin: '0 auto',
              }}
            >
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 'normal', color: theme.palette.text.primary, textAlign: 'center' }}>
                Would you like to explore advanced FleetGPT features?
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(2), width: '100%', justifyContent: 'center' }}>
                <Button onClick={handleShowInterest} variant="contained" color="primary" sx={{ textTransform: 'none' }}>
                  Yes, I'm Interested
                </Button>
                <Button onClick={() => {
                  setIsNoThanksAcknowledged(true);
                  sendEmptyEmailPayloadAndClose(true);
                  setTimeout(() => {
                    onClose();
                  }, 10000);
                }} variant="outlined" color="secondary" sx={{ textTransform: 'none' }}>
                  No, Thanks
                </Button>
              </div>
            </Box>
          )}

          {/* Integrated Email Collection Prompt */}
          {showEmailInputPrompt && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
                mt: 2,
                bgcolor: isDark ? "#2a2a2a" : "#f0f0f0",
                borderRadius: 4,
                boxShadow: theme.shadows[3],
                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                width: '80%',
                margin: '0 auto',
              }}
            >
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 'normal', color: theme.palette.text.primary, textAlign: 'center' }}>
                To get started with advanced features, please provide your email address:
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="email"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={false} // This TextField should always be enabled when shown
                sx={{
                  mb: 2,
                  '& .MuiInputBase-input': {
                    fontWeight: 'normal',
                  },
                  '& .Mui-disabled': {
                    backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(200,200,200,0.3)',
                    color: isDark ? '#aaa' : '#555',
                    '-webkit-text-fill-color': isDark ? '#aaa' : '#555',
                    opacity: 0.7,
                  },
                }}
              />
              <Button onClick={handleEmailSubmit} variant="contained" color="primary" sx={{ textTransform: 'none', alignSelf: 'center' }}>
                Submit
              </Button>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Conditionally rendered */}
        {shouldShowChatInput && (
          <div className="border-t px-4 py-3" style={{ backgroundColor: isDark ? "#1e1e1e" : "#f9f9f9", borderColor: isDark ? "#333" : "#ddd" }}>
            <div className="rounded-lg px-4 py-2 w-full" style={{ backgroundColor: isDark ? "#2a2a2a" : "#fff", border: `1px solid ${isDark ? "#444" : "#ccc"}` }}>
              <input
                type="text"
                value={inputValue}
                onChange={e => {
                  const sanitizedValue = DOMPurify.sanitize(e.target.value, {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: [],
                  });
                  setInputValue(sanitizedValue);

                  if (noThanksTimeoutRef.current) {
                    clearTimeout(noThanksTimeoutRef.current);
                    noThanksTimeoutRef.current = null;
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Message here"
                className="w-full text-sm bg-transparent border-none outline-none mb-2"
                style={{ color: isDark ? "#fff" : "#000" }}
                disabled={isInputDisabled}
              />
              <div className="flex justify-between items-center">
                <IconButton size="small" disabled={isInputDisabled}>
                  <img src={Attachment} alt="Attachment" className="h-5 w-5" style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }} />
                </IconButton>
                <Button
                  variant="text"
                  disabled={!inputValue.trim() && !showEmailInputPrompt || isInputDisabled}
                  onClick={handleSend}
                  sx={{ textTransform: "none", color: "#5547DD", display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  Send
                  <img src={SendIcon} alt="Send" className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Paper>
    </>
  );
}